/**
 * Document platform — the lifecycle every business document follows, the
 * workflow actions and the permission each one requires, and the registry of
 * document types (numbering prefixes, period rules, posting behaviour).
 */
import type { ModuleKey, StandardAction } from './permissions';

export const DOC_STATUSES = ['DRAFT', 'PENDING', 'SENT_BACK', 'APPROVED', 'POSTED', 'CANCELLED', 'REVERSED'] as const;
export type DocStatus = (typeof DOC_STATUSES)[number];

export const DOC_STATUS_LABELS: Record<DocStatus, string> = {
  DRAFT: 'Draft',
  PENDING: 'Pending approval',
  SENT_BACK: 'Sent for rectification',
  APPROVED: 'Approved',
  POSTED: 'Posted',
  CANCELLED: 'Cancelled',
  REVERSED: 'Reversed',
};

/** Badge tone per status (UI). */
export const DOC_STATUS_TONE: Record<DocStatus, 'neutral' | 'primary' | 'warning' | 'success' | 'danger'> = {
  DRAFT: 'neutral',
  PENDING: 'warning',
  SENT_BACK: 'warning',
  APPROVED: 'primary',
  POSTED: 'success',
  CANCELLED: 'danger',
  REVERSED: 'danger',
};

export const WORKFLOW_ACTIONS = ['submit', 'approve', 'send_back', 'post', 'cancel', 'reverse', 'audit'] as const;
export type WorkflowAction = (typeof WORKFLOW_ACTIONS)[number];

export interface WorkflowActionDef {
  label: string;
  /** Statuses the action may start from. */
  from: readonly DocStatus[];
  /** Resulting status (audit leaves the status unchanged). */
  to: DocStatus | null;
  /** Permission action on the document's screen (`module.screen.<permission>`). */
  permission: StandardAction | 'audit';
  /** A comment is mandatory (send back, cancel, reverse). */
  commentRequired: boolean;
}

/**
 * The single lifecycle. Posted documents are never edited: a correction is a
 * reversal. A posted document can only be reversed, not cancelled.
 */
export const WORKFLOW: Record<WorkflowAction, WorkflowActionDef> = {
  submit: { label: 'Submit for approval', from: ['DRAFT', 'SENT_BACK'], to: 'PENDING', permission: 'create', commentRequired: false },
  approve: { label: 'Approve', from: ['PENDING'], to: 'APPROVED', permission: 'approve', commentRequired: false },
  send_back: { label: 'Send back for correction', from: ['PENDING', 'APPROVED'], to: 'SENT_BACK', permission: 'reject', commentRequired: true },
  post: { label: 'Post', from: ['APPROVED'], to: 'POSTED', permission: 'post', commentRequired: false },
  cancel: { label: 'Cancel', from: ['DRAFT', 'PENDING', 'SENT_BACK', 'APPROVED'], to: 'CANCELLED', permission: 'cancel', commentRequired: true },
  reverse: { label: 'Reverse', from: ['POSTED'], to: 'REVERSED', permission: 'unpost', commentRequired: true },
  audit: { label: 'Mark as audited', from: ['POSTED'], to: null, permission: 'audit', commentRequired: false },
};

/** Statuses in which the document's content may still be edited. */
export const EDITABLE_STATUSES: readonly DocStatus[] = ['DRAFT', 'SENT_BACK'];

export const actionsFrom = (status: DocStatus): WorkflowAction[] =>
  WORKFLOW_ACTIONS.filter((a) => WORKFLOW[a].from.includes(status));

export type ResetPolicy = 'MONTHLY' | 'YEARLY' | 'NEVER';

export interface DocumentTypeDef {
  /** Stable key, `<module>.<name>`. */
  key: string;
  label: string;
  module: ModuleKey;
  /** Permission screen the document belongs to. */
  screen: string;
  /** Allowed number prefixes; the first is the default (e.g. receipts: BRV for bank, CRV for cash). */
  prefixes: readonly string[];
  reset: ResetPolicy;
  /** Digits of the running number. Never fewer than 5 for new documents. */
  digits: number;
  /** First number of each period (the old system's vouchers count from 1001 every month). */
  startAt: number;
  /** Approval also posts (no separate Post step). */
  postOnApprove: boolean;
  /** Days a document date may lie in the future (cheque registers, plans). */
  futureDays: number;
  /** False for documents that stay APPROVED until used (requisitions, orders); they are never posted. */
  postable: boolean;
  /**
   * Another department posts it: once approved, "post" and "send back" are governed by this screen's
   * permissions (e.g. the payroll run approved by HR becomes a salary voucher Accounts approves).
   */
  postBy?: { module: ModuleKey; screen: string };
}

const doc = (
  key: string,
  label: string,
  module: ModuleKey,
  screen: string,
  prefixes: string[],
  opts: Partial<Pick<DocumentTypeDef, 'reset' | 'digits' | 'startAt' | 'postOnApprove' | 'futureDays' | 'postable' | 'postBy'>> = {},
): DocumentTypeDef => ({
  key,
  label,
  module,
  screen,
  prefixes,
  reset: opts.reset ?? 'MONTHLY',
  digits: opts.digits ?? 5,
  startAt: opts.startAt ?? 1,
  postOnApprove: opts.postOnApprove ?? true,
  futureDays: opts.futureDays ?? 0,
  postable: opts.postable ?? true,
  ...(opts.postBy ? { postBy: opts.postBy } : {}),
});

/** Vouchers keep the old system's series: monthly, counting from 1001 (e.g. CPV-2026-10-1041). */
const OLD_SERIES = { digits: 4, startAt: 1001 } as const;

/** Registry of every numbered business document. Prefixes keep the legacy conventions. */
export const DOCUMENT_TYPES: readonly DocumentTypeDef[] = [
  // Accounting
  doc('acct.journal', 'Journal Voucher', 'accounting', 'journal_vouchers', ['JV'], OLD_SERIES),
  doc('acct.receipt', 'Receipt', 'accounting', 'receipts', ['BRV', 'CRV'], OLD_SERIES),
  doc('acct.payment', 'Payment', 'accounting', 'payments', ['BPV', 'CPV'], OLD_SERIES),
  doc('acct.pdc_received', 'Post-dated Cheque Received', 'accounting', 'receipts', ['PDCR'], OLD_SERIES),
  doc('acct.pdc_issued', 'Post-dated Cheque Issued', 'accounting', 'payments', ['PDCP'], OLD_SERIES),
  doc('acct.contra', 'Contra Voucher', 'accounting', 'journal_vouchers', ['CTR'], OLD_SERIES),
  doc('acct.opening', 'Opening Balances', 'accounting', 'journal_vouchers', ['OB'], { reset: 'NEVER' }),
  /** Vouchers migrated from the old system that belong to modules not yet live (sales/purchase vouchers). */
  doc('acct.legacy_voucher', 'Legacy Voucher', 'accounting', 'journal_vouchers', ['LSV', 'LSR', 'LPV', 'LPR', 'IPV']),
  // Purchase & inventory
  doc('inv.purchase_requisition', 'Purchase Requisition', 'inventory', 'purchase_requisitions', ['PR'], { postOnApprove: false, postable: false }),
  doc('inv.purchase_order', 'Purchase Order', 'inventory', 'purchase_orders', ['PO'], { postOnApprove: false, postable: false }),
  doc('inv.goods_receipt', 'Goods Receipt', 'inventory', 'goods_receipts', ['GRN']),
  doc('inv.purchase_bill', 'Supplier Bill', 'inventory', 'purchase_bills', ['LPV'], OLD_SERIES),
  doc('inv.purchase_return', 'Purchase Return', 'inventory', 'purchase_returns', ['LPR'], OLD_SERIES),
  doc('inv.stock_issue', 'Stock Issue', 'inventory', 'stock_issues', ['SIN']),
  doc('inv.stock_return', 'Return to Store', 'inventory', 'stock_issues', ['SRN']),
  doc('inv.stock_transfer', 'Stock Transfer', 'inventory', 'stock_transfers', ['STR']),
  doc('inv.stock_adjustment', 'Stock Adjustment', 'inventory', 'stock_adjustments', ['ADJ']),
  /** Opening stock (migration, physical count at cut-over). */
  doc('inv.item_conversion', 'Item Conversion', 'inventory', 'stock_adjustments', ['CNV']),
  doc('inv.opening_stock', 'Opening Stock', 'inventory', 'stock_adjustments', ['OS'], { reset: 'NEVER' }),
  // Sales & invoicing
  doc('pre.inquiry', 'Enquiry', 'sales', 'enquiries', ['INQ'], { postOnApprove: false, postable: false, reset: 'YEARLY', futureDays: 365 }),
  doc('sales.quotation', 'Quotation', 'sales', 'quotations', ['QT'], { postOnApprove: false, postable: false }),
  doc('sales.order', 'Sales Order', 'sales', 'sales_orders', ['SO'], { postOnApprove: false, reset: 'YEARLY', postable: false }),
  doc('sales.delivery', 'Delivery Note', 'invoicing', 'delivery_notes', ['DN']),
  doc('sales.invoice', 'Tax Invoice', 'invoicing', 'tax_invoices', ['INV'], { reset: 'YEARLY' }),
  doc('sales.credit_note', 'Credit Note', 'invoicing', 'credit_notes', ['CN']),
  // HR & payroll
  doc('hr.leave_request', 'Leave Request', 'hr', 'leave_requests', ['LV'], { reset: 'YEARLY', futureDays: 365 }),
  doc('hr.loan', 'Advance / Loan', 'hr', 'loans', ['ADV', 'LOAN'], { reset: 'YEARLY' }),
  doc('hr.payroll_run', 'Payroll Run', 'hr', 'payroll_runs', ['PAY'], { reset: 'YEARLY', futureDays: 31, postOnApprove: false, postBy: { module: 'accounting', screen: 'salary_vouchers' } }),
  doc('hr.final_settlement', 'Final Settlement', 'hr', 'final_settlements', ['EOS'], { reset: 'YEARLY', futureDays: 62 }),
  // Production
  doc('mfg.work_order', 'Production Job', 'manufacturing', 'work_orders', ['JOB'], { postOnApprove: false, reset: 'YEARLY', futureDays: 365, postable: false }),
  doc('mfg.material_request', 'Material Requisition (MRN)', 'manufacturing', 'material_requisitions', ['MRN'], { postOnApprove: false, postable: false }),
  // Planning Studio: approval writes the plan into the job's materials (the demand order requisitions draw from).
  doc('mfg.material_plan', 'Material Plan (furniture job)', 'manufacturing', 'material_plans', ['MPL'], { postOnApprove: true, postable: false, reset: 'YEARLY', futureDays: 365 }),
  doc('mfg.fg_receipt', 'Finished Goods Receipt', 'manufacturing', 'fg_receipts', ['FGR']),
  doc('mtr.production_order', 'Mattress Production Order', 'mattress', 'production_orders', ['MPO'], { postOnApprove: false, postable: false, futureDays: 365 }),
  doc('mtr.production_entry', 'Mattress Production Entry', 'mattress', 'production_entries', ['MPE']),
  doc('mtr.fg_receipt', 'Mattress Finished Goods Receipt', 'mattress', 'production_entries', ['MFR']),
  doc('svc.ticket', 'Service Ticket', 'service', 'service_tickets', ['SRV'], { postOnApprove: false, postable: false, reset: 'YEARLY', futureDays: 0 }),
  doc('ins.order', 'Installation Order', 'installation', 'installation_orders', ['INS'], { postOnApprove: false, postable: false, reset: 'YEARLY', futureDays: 365 }),
  doc('inv.stock_count', 'Stock Count', 'inventory', 'stock_counts', ['CNT'], { postOnApprove: false, postable: false }),
  doc('inv.gate_pass', 'Inward Gate Pass', 'inventory', 'gate_passes', ['IGP'], { postOnApprove: false, postable: false }),
  /** The store's request for items an order needs beyond its production plan (taken up by the order's requisition). */
  doc('inv.special_request', 'Special Request Note', 'inventory', 'special_requests', ['SRN'], { postOnApprove: false, postable: false }),
  /** Goods leaving the premises: returns to vendors, items sent for repair or outside work. */
  doc('inv.out_gate_pass', 'Outward Gate Pass', 'inventory', 'out_gate_passes', ['OGP'], { postOnApprove: false, postable: false }),
];

export const DOCUMENT_TYPES_BY_KEY: Readonly<Record<string, DocumentTypeDef>> = Object.fromEntries(DOCUMENT_TYPES.map((d) => [d.key, d]));

/** Formats a document number: `PFX-YYYY-MM-00001` (monthly), `PFX-YYYY-00001` (yearly), `PFX-00001` (never). */
export function formatDocNumber(prefix: string, reset: ResetPolicy, date: string, seq: number, digits: number): string {
  const n = String(seq).padStart(digits, '0');
  const [y, m] = date.split('-');
  if (reset === 'MONTHLY') return `${prefix}-${y}-${m}-${n}`;
  if (reset === 'YEARLY') return `${prefix}-${y}-${n}`;
  return `${prefix}-${n}`;
}

/** Sequence period key for a document date (`2026-10-04` → `2026-10` / `2026` / `ALL`). */
export function periodKey(reset: ResetPolicy, date: string): string {
  if (reset === 'MONTHLY') return date.slice(0, 7);
  if (reset === 'YEARLY') return date.slice(0, 4);
  return 'ALL';
}
